import { useState, useEffect, useCallback } from 'react';
import API, { formatApiError } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    FileText, Plus, Edit, Trash2, Eye, Save, X, Copy, CheckCircle
} from 'lucide-react';

const CATEGORIES = ['agreement', 'disclosure', 'amendment', 'notice', 'other'];

export default function TemplatesPage() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [preview, setPreview] = useState(null);
    const [showCreate, setShowCreate] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await API.get('/templates/');
            setTemplates(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this template?')) return;
        try {
            await API.delete(`/templates/${id}`);
            load();
        } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    };

    const handlePreview = async (id) => {
        try {
            const { data } = await API.post(`/templates/${id}/preview`, {
                buyer_name: "John Doe", seller_name: "Jane Smith",
                property_address: "123 Main St, Houston, TX",
                purchase_price: "$250,000", down_payment: "$25,000",
                monthly_payment: "$1,500", interest_rate: "8%",
                date: new Date().toLocaleDateString(),
            });
            setPreview(data);
        } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    };

    if (loading) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="templates-page">
            <Navbar variant="light" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-medium text-zinc-900 font-['Outfit']" data-testid="templates-title">Document Templates</h1>
                        <p className="text-sm text-zinc-500 mt-1">Customize agreements, disclosures, and notices for your deals.</p>
                    </div>
                    <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm" data-testid="create-template-btn">
                        <Plus className="w-4 h-4 mr-2" /> New Template
                    </Button>
                </div>

                {templates.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center shadow-sm">
                        <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-zinc-900 mb-2 font-['Outfit']">No Templates Yet</h3>
                        <p className="text-sm text-zinc-500 mb-4">Create your first document template to streamline deal processing.</p>
                        <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">
                            <Plus className="w-4 h-4 mr-2" /> Create Template
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {templates.map(t => (
                            <div key={t.id} className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5 hover:shadow-md transition-shadow"
                                data-testid={`template-card-${t.id}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <div className="text-sm font-medium text-zinc-900">{t.name}</div>
                                        <Badge className="mt-1 bg-zinc-100 text-zinc-600 text-[10px]">{t.category}</Badge>
                                    </div>
                                    <FileText className="w-5 h-5 text-zinc-300" />
                                </div>
                                {t.description && <p className="text-xs text-zinc-500 mb-3 line-clamp-2">{t.description}</p>}
                                <div className="text-xs text-zinc-400 mb-3">
                                    Variables: {(t.variables || []).join(', ') || 'None'}
                                </div>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="outline" onClick={() => handlePreview(t.id)} className="text-xs border-zinc-200">
                                        <Eye className="w-3 h-3 mr-1" /> Preview
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditing(t)} className="text-xs border-zinc-200">
                                        <Edit className="w-3 h-3 mr-1" /> Edit
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => handleDelete(t.id)} className="text-xs border-red-200 text-red-600 hover:bg-red-50">
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {(showCreate || editing) && (
                <TemplateEditor
                    template={editing}
                    onClose={() => { setShowCreate(false); setEditing(null); load(); }}
                />
            )}

            {preview && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-zinc-900 font-['Outfit']">Template Preview</h2>
                            <button onClick={() => setPreview(null)}><X className="w-5 h-5 text-zinc-400" /></button>
                        </div>
                        <div className="bg-zinc-50 rounded-lg p-6 text-sm leading-relaxed whitespace-pre-wrap font-mono">
                            {preview.preview}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TemplateEditor({ template, onClose }) {
    const [form, setForm] = useState({
        name: template?.name || '',
        category: template?.category || 'agreement',
        description: template?.description || '',
        content: template?.content || '',
        variables: (template?.variables || []).join(', '),
    });
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (!form.name || !form.content) return;
        setLoading(true);
        try {
            const payload = {
                ...form,
                variables: form.variables.split(',').map(v => v.trim()).filter(Boolean),
            };
            if (template) {
                await API.put(`/templates/${template.id}`, payload);
            } else {
                await API.post('/templates/', payload);
            }
            onClose();
        } catch (err) {
            alert(formatApiError(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="template-editor-modal">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-zinc-900 font-['Outfit']">
                        {template ? 'Edit Template' : 'Create Template'}
                    </h2>
                    <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
                </div>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Template Name</Label>
                            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="Purchase Agreement" className="border-zinc-200 text-sm" data-testid="template-name-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Category</Label>
                            <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                                <SelectTrigger className="border-zinc-200 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Description</Label>
                        <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="Standard owner-finance purchase agreement" className="border-zinc-200 text-sm" />
                    </div>
                    <div>
                        <Label className="text-xs">Variables (comma separated)</Label>
                        <Input value={form.variables} onChange={e => setForm(f => ({ ...f, variables: e.target.value }))}
                            placeholder="buyer_name, seller_name, property_address, purchase_price"
                            className="border-zinc-200 text-sm" />
                        <p className="text-xs text-zinc-400 mt-1">Use {"{{variable_name}}"} in content. Available: buyer_name, seller_name, property_address, purchase_price, etc.</p>
                    </div>
                    <div>
                        <Label className="text-xs">Content</Label>
                        <Textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                            placeholder={"OWNER FINANCE AGREEMENT\n\nThis Agreement is entered into on {{date}} between:\n\nSELLER: {{seller_name}}\nBUYER: {{buyer_name}}\n\nProperty: {{property_address}}\nPurchase Price: {{purchase_price}}\n..."}
                            className="border-zinc-200 text-sm min-h-[300px] font-mono" data-testid="template-content-input" />
                    </div>
                </div>
                <div className="flex gap-2 mt-6 pt-4 border-t border-zinc-200">
                    <Button onClick={submit} disabled={loading || !form.name || !form.content}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="template-save-btn">
                        <Save className="w-4 h-4 mr-2" /> {loading ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
                    </Button>
                    <Button variant="outline" onClick={onClose} className="border-zinc-200">Cancel</Button>
                </div>
            </div>
        </div>
    );
}
